/**
 * 登录页面
 *
 * 基于 page-dedsign.pen 原型设计
 * 左侧：深色品牌展示区
 * 右侧：白色登录表单
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, message } from 'antd';
import {
    UserOutlined,
    LockOutlined,
    RobotOutlined,
    ThunderboltOutlined,
    SafetyOutlined,
    ApiOutlined,
} from '@ant-design/icons';
import { useAuthStore } from '../stores/authStore';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { login, isLoading, error, clearError, isAuthenticated } = useAuthStore();
    const [form] = Form.useForm();

    // 已登录直接跳转
    useEffect(() => {
        if (isAuthenticated) {
            navigate('/admin/dashboard', { replace: true });
        }
    }, [isAuthenticated, navigate]);

    // 显示错误
    useEffect(() => {
        if (error) {
            message.error(error);
            clearError();
        }
    }, [error, clearError]);

    const handleSubmit = async (values: { username: string; password: string }) => {
        const success = await login(values.username, values.password);
        if (success) {
            message.success('登录成功');
            navigate('/admin/dashboard', { replace: true });
        }
    };

    return (
        <div style={{ display: 'flex', height: '100vh', width: '100vw' }}>
            {/* 左侧品牌展示区 */}
            <div
                style={{
                    flex: '0 0 480px',
                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '60px 48px',
                    color: '#fff',
                }}
            >
                {/* Logo + 标题 */}
                <div style={{ textAlign: 'center', marginBottom: 48 }}>
                    <div
                        style={{
                            width: 72,
                            height: 72,
                            borderRadius: 16,
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            boxShadow: '0 8px 25px rgba(37, 99, 235, 0.4)',
                        }}
                    >
                        <RobotOutlined style={{ fontSize: 36, color: '#fff' }} />
                    </div>
                    <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: '#fff' }}>
                        智能客服管理系统
                    </h1>
                    <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                        AI 驱动的新一代客户服务平台
                    </p>
                </div>

                {/* 产品特性 */}
                <div style={{ width: '100%', maxWidth: 320 }}>
                    {[
                        {
                            icon: <ThunderboltOutlined style={{ fontSize: 20, color: '#60a5fa' }} />,
                            title: '意图识别 + RAG',
                            desc: '精准理解用户意图，快速检索知识库',
                        },
                        {
                            icon: <ApiOutlined style={{ fontSize: 20, color: '#60a5fa' }} />,
                            title: '混合检索引擎',
                            desc: '向量与关键词融合检索，精准定位答案',
                        },
                        {
                            icon: <SafetyOutlined style={{ fontSize: 20, color: '#60a5fa' }} />,
                            title: '三层对话记忆',
                            desc: '从单轮交互到长期用户画像，提供连贯流畅的对话体验',
                        },
                    ].map((item, i) => (
                        <div
                            key={i}
                            style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 14,
                                marginBottom: 24,
                                padding: '16px 18px',
                                background: 'rgba(255,255,255,0.05)',
                                borderRadius: 12,
                                border: '1px solid rgba(255,255,255,0.08)',
                            }}
                        >
                            <div
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 10,
                                    background: 'rgba(59, 130, 246, 0.15)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                {item.icon}
                            </div>
                            <div>
                                <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                                    {item.title}
                                </div>
                                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', lineHeight: 1.4 }}>
                                    {item.desc}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 右侧登录表单 */}
            <div
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fafafa',
                }}
            >
                <div style={{ width: 400, padding: '0 24px' }}>
                    <h2
                        style={{
                            fontSize: 24,
                            fontWeight: 700,
                            color: '#18181b',
                            marginBottom: 8,
                        }}
                    >
                        欢迎登录
                    </h2>
                    <p style={{ fontSize: 14, color: '#71717a', marginBottom: 32 }}>
                        请输入您的账号和密码
                    </p>

                    <Form
                        form={form}
                        name="login"
                        onFinish={handleSubmit}
                        layout="vertical"
                        size="large"
                        requiredMark={false}
                    >
                        <Form.Item
                            name="username"
                            label={<span style={{ fontWeight: 500, color: '#3f3f46' }}>用户名</span>}
                            rules={[
                                { required: true, message: '请输入用户名' },
                                { min: 3, message: '用户名至少 3 个字符' },
                            ]}
                        >
                            <Input
                                prefix={<UserOutlined style={{ color: '#a1a1aa' }} />}
                                placeholder="请输入用户名或邮箱"
                                style={{ height: 44, borderRadius: 8 }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="password"
                            label={<span style={{ fontWeight: 500, color: '#3f3f46' }}>密码</span>}
                            rules={[
                                { required: true, message: '请输入密码' },
                                { min: 8, message: '密码至少 8 个字符' },
                            ]}
                        >
                            <Input.Password
                                prefix={<LockOutlined style={{ color: '#a1a1aa' }} />}
                                placeholder="请输入密码"
                                style={{ height: 44, borderRadius: 8 }}
                            />
                        </Form.Item>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginBottom: 24,
                            }}
                        >
                            <a style={{ fontSize: 13, color: '#3b82f6' }}>忘记密码？</a>
                        </div>

                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={isLoading}
                            block
                            style={{
                                height: 44,
                                borderRadius: 8,
                                fontWeight: 600,
                                fontSize: 15,
                                background: '#2563eb',
                            }}
                        >
                            登录
                        </Button>
                    </Form>

                </div>
            </div>
        </div>
    );
};

export default LoginPage;
